import React from "react";
import { Link } from "react-router-dom";
import Card from "../../../../components/card";
import { ClientHours } from "../../../../interfaces/Dashboard";
import DeltaChip from "./DeltaChip";

/**
 * Horas e faturamento por cliente no mês — uma tabela só.
 *
 * Juntar horas, Δ contra o período anterior, faturado/previsto e estágio da cobrança numa linha é
 * o que permite ler a carteira de cima a baixo: a linha com muitas horas e estágio "Sem OS" é
 * exatamente a que paga a reunião de segunda.
 */

const STAGE: Record<string, { label: string; cls: string }> = {
  received: { label: "Recebida", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  to_invoice: { label: "A faturar", cls: "bg-dv-blue-500/15 text-dv-blue-600 dark:text-dv-cyan-300" },
  pending: { label: "Sem OS", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  none: { label: "—", cls: "text-gray-400 dark:text-gray-500" },
};

const fmtMoneyK = (v: number) =>
  v >= 1000
    ? `R$ ${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil`
    : `R$ ${v.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;

const ClientHoursTable: React.FC<{ rows: ClientHours[]; previousLabel: string }> = ({ rows, previousLabel }) => (
  <Card extra="p-5">
    <h3 className="text-base font-bold text-navy-700 dark:text-white">Horas e faturamento por cliente</h3>
    <p className="mb-4 text-xs text-gray-700 dark:text-gray-300">
      Horas do mês com a variação contra o mesmo período de {previousLabel}, faturado contra o
      previsto e o estágio da cobrança.
    </p>

    {rows.length === 0 ? (
      <div className="rounded-2xl border border-dashed border-gray-200 py-8 text-center dark:border-gray-700">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Sem horas no período.</p>
      </div>
    ) : (
      <div className="-mx-1.5 overflow-x-auto px-1.5">
        <table className="w-full min-w-[560px] border-collapse text-sm">
          <thead>
            <tr>
              {[
                { l: "Cliente", n: false },
                { l: "Horas", n: true },
                { l: `vs ${previousLabel}`, n: true },
                { l: "Faturado / previsto", n: false },
                { l: "Estágio", n: true },
              ].map((h) => (
                <th
                  key={h.l}
                  className={`border-b border-gray-100 px-2.5 pb-2.5 text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:border-white/10 dark:text-gray-400 ${
                    h.n ? "text-right" : "text-left"
                  }`}
                >
                  {h.l}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const stage = STAGE[c.billingStage] ?? STAGE.none;
              const pct = c.forecast > 0 ? Math.min(100, (c.realized / c.forecast) * 100) : 0;
              return (
                <tr
                  key={c.clientId}
                  className="border-b border-gray-50 transition-colors last:border-0 hover:bg-dv-cyan-500/5 dark:border-white/5"
                >
                  <td className="px-2.5 py-2.5 font-semibold text-navy-700 dark:text-white">{c.clientName}</td>
                  <td className="px-2.5 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {c.hoursMtd.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h
                  </td>
                  <td className="px-2.5 py-2.5 text-right">
                    <DeltaChip value={c.hoursDeltaPct} />
                  </td>
                  <td className="px-2.5 py-2.5">
                    {c.forecast > 0 || c.realized > 0 ? (
                      <div className="w-[130px]">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-dv-blue-500 to-dv-cyan-400"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="mt-1 text-[10.5px] tabular-nums text-gray-500 dark:text-gray-400">
                          {fmtMoneyK(c.realized)} / {fmtMoneyK(c.forecast)}
                        </p>
                      </div>
                    ) : (
                      <span className="text-[10.5px] text-gray-400 dark:text-gray-500">não faturável</span>
                    )}
                  </td>
                  <td className="px-2.5 py-2.5 text-right">
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${stage.cls}`}>
                      {stage.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}

    <Link
      to="/admin/financial"
      className="mt-4 text-xs font-semibold text-dv-blue-600 hover:underline dark:text-dv-cyan-300"
    >
      Ver no Financeiro →
    </Link>
  </Card>
);

export default ClientHoursTable;
